import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { clearDashboardSessions, getActiveDashboardSession } from '../utils/session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';

type UserStatus = 'pending' | 'approved' | 'disapproved';
type StatusFilter = UserStatus | 'all';

interface StaffUser {
  id: number;
  firstName: string;
  lastName: string;
  department: string | null;
  email: string;
  role: 'staff' | 'admin';
  status: UserStatus;
  approvalEmailSent: boolean;
  approvalEmailError: string | null;
  approvalEmailSentAt: string | null;
  approvedAt: string | null;
  reviewedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  submissionCount?: number;
  lastSubmittedAt?: string | null;
}

interface UsersResponse {
  success: boolean;
  users: StaffUser[];
  summary: {
    pending: number;
    approved: number;
    disapproved: number;
    total: number;
  };
}

export const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dashboardSession = getActiveDashboardSession();
  const dashboardUser = dashboardSession?.user;
  const isAdmin = dashboardSession?.role === 'admin';
  const initialStatus = useMemo<StatusFilter>(() => {
    const raw = searchParams.get('status');
    return raw === 'pending' || raw === 'approved' || raw === 'disapproved' ? raw : 'all';
  }, [searchParams]);
  const focusUserId = searchParams.get('focusUser');

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [searchInput, setSearchInput] = useState('');
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [summary, setSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [promotingUserId, setPromotingUserId] = useState<number | null>(null);
  const [demotingUserId, setDemotingUserId] = useState<number | null>(null);

  const redirectToLogin = () => {
    navigate(`/admin/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`, {
      replace: true,
    });
  };

  const fetchUsers = async (searchValue: string = searchInput) => {
    try {
      setLoading(true);
      setError('');

      const token = dashboardSession?.token;
      if (!token) {
        redirectToLogin();
        return;
      }

      const params: Record<string, string> = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchValue.trim()) {
        params.search = searchValue.trim();
      }

      const response = await axios.get<UsersResponse>(`${API_BASE_URL}/auth/admin/users`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUsers(response.data.users);
      setSummary(response.data.summary);
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        redirectToLogin();
      } else if (err.response?.status === 403) {
        navigate('/admin/submissions', { replace: true });
      } else {
        setError(err.response?.data?.error || 'Failed to load staff users');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dashboardSession?.token) {
      redirectToLogin();
      return;
    }

    if (!isAdmin) {
      navigate('/admin/submissions', { replace: true });
      return;
    }

    fetchUsers();
  }, [dashboardSession?.token, isAdmin, statusFilter, navigate]);

  const handleLogout = () => {
    const nextPath = dashboardSession?.role === 'staff' ? '/staff/login' : '/admin/login';
    clearDashboardSessions();
    navigate(nextPath, { replace: true });
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetchUsers(searchInput);
  };

  const updateStatus = async (userId: number, status: Exclude<UserStatus, 'pending'>) => {
    try {
      setUpdatingUserId(userId);
      setError('');
      const token = dashboardSession?.token;
      if (!token) {
        redirectToLogin();
        return;
      }

      await axios.patch(
        `${API_BASE_URL}/auth/admin/users/${userId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await fetchUsers();
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        redirectToLogin();
      } else if (err.response?.status === 403) {
        navigate('/admin/submissions', { replace: true });
      } else {
        setError(err.response?.data?.error || 'Failed to update user status');
      }
    } finally {
      setUpdatingUserId(null);
    }
  };

  const deleteUser = async (user: StaffUser) => {
    const confirmed = window.confirm(
      `Delete staff user "${user.firstName} ${user.lastName}" (${user.email})? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingUserId(user.id);
      setError('');
      const token = dashboardSession?.token;
      if (!token) {
        redirectToLogin();
        return;
      }

      await axios.delete(`${API_BASE_URL}/auth/admin/users/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await fetchUsers();
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        redirectToLogin();
      } else if (err.response?.status === 403) {
        navigate('/admin/submissions', { replace: true });
      } else {
        setError(err.response?.data?.error || 'Failed to delete user');
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const resetPassword = async (user: StaffUser) => {
    const newPassword = window.prompt(
      `Set new password for ${user.firstName} ${user.lastName} (${user.email}):\n\nMust be at least 8 characters.`
    );
    if (!newPassword) return;
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }

    try {
      setResettingUserId(user.id);
      setError('');
      const token = dashboardSession?.token;
      if (!token) { redirectToLogin(); return; }

      await axios.post(
        `${API_BASE_URL}/auth/admin/users/${user.id}/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Password reset successfully for ${user.firstName} ${user.lastName}.`);
    } catch (err: any) {
      if (err.response?.status === 401) { clearDashboardSessions(); redirectToLogin(); }
      else { setError(err.response?.data?.error || 'Failed to reset password'); }
    } finally {
      setResettingUserId(null);
    }
  };

  const makeAdmin = async (user: StaffUser) => {
    if (!window.confirm(`Promote "${user.firstName} ${user.lastName}" (${user.email}) to Admin?\nThey will have full admin access.`)) return;
    try {
      setPromotingUserId(user.id);
      setError('');
      const token = dashboardSession?.token;
      if (!token) { redirectToLogin(); return; }
      await axios.patch(`${API_BASE_URL}/auth/admin/users/${user.id}/make-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchUsers();
    } catch (err: any) {
      if (err.response?.status === 401) { clearDashboardSessions(); redirectToLogin(); }
      else { setError(err.response?.data?.error || 'Failed to promote user'); }
    } finally {
      setPromotingUserId(null);
    }
  };

  const removeAdmin = async (user: StaffUser) => {
    if (!window.confirm(`Remove admin role from "${user.firstName} ${user.lastName}" (${user.email})?\nThey will become a regular staff member.`)) return;
    try {
      setDemotingUserId(user.id);
      setError('');
      const token = dashboardSession?.token;
      if (!token) { redirectToLogin(); return; }
      await axios.patch(`${API_BASE_URL}/auth/admin/users/${user.id}/remove-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchUsers();
    } catch (err: any) {
      if (err.response?.status === 401) { clearDashboardSessions(); redirectToLogin(); }
      else { setError(err.response?.data?.error || 'Failed to remove admin role'); }
    } finally {
      setDemotingUserId(null);
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return 'Never';
    return new Date(value).toLocaleString();
  };

  const badgeClasses = (status: UserStatus) => {
    if (status === 'approved') return 'bg-emerald-100 text-emerald-800';
    if (status === 'disapproved') return 'bg-red-100 text-red-800';
    return 'bg-amber-100 text-amber-800';
  };

  const notificationLabel = (user: StaffUser) => {
    if (user.approvalEmailSent) return 'Sent';
    if (user.approvalEmailError) return 'Failed';
    return 'Not sent';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Users</h1>
              <p className="text-sm text-gray-600">
                Welcome, {dashboardUser?.firstName || 'Admin'} {dashboardUser?.lastName || ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/admin/submissions" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                Submissions
              </Link>
              <Link to="/admin/referrals" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                Referrals
              </Link>
              <Link to="/admin/users" className="text-sm text-blue-700 font-medium px-3 py-2 rounded-md bg-blue-50">
                Users
              </Link>
              <Link to="/submit-forms" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                Main Form
              </Link>
              <button onClick={handleLogout} className="text-sm text-red-600 hover:text-red-700 px-3 py-2">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="rounded-2xl bg-white p-5 shadow-sm border">
            <p className="text-sm text-gray-500">Total Staff Requests</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{summary.total}</p>
          </div>
          <div className="rounded-2xl bg-amber-50 p-5 shadow-sm border border-amber-100">
            <p className="text-sm text-amber-700">Pending</p>
            <p className="mt-2 text-3xl font-bold text-amber-900">{summary.pending}</p>
          </div>
          <div className="rounded-2xl bg-emerald-50 p-5 shadow-sm border border-emerald-100">
            <p className="text-sm text-emerald-700">Approved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-900">{summary.approved}</p>
          </div>
          <div className="rounded-2xl bg-red-50 p-5 shadow-sm border border-red-100">
            <p className="text-sm text-red-700">Disapproved</p>
            <p className="mt-2 text-3xl font-bold text-red-900">{summary.disapproved}</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-sm border">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {(['all', 'pending', 'approved', 'disapproved'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    statusFilter === status
                      ? 'bg-slate-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 lg:min-w-[360px]">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, department, or email"
                className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Search
              </button>
            </form>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="rounded-2xl bg-white shadow-sm border overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading staff users...</div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center text-gray-500">No staff users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Staff</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Forms</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Edited</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Notification</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Requested</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Login</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {users.map((user) => {
                    const isFocused = focusUserId === String(user.id);
                    return (
                      <tr key={user.id} className={isFocused ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-4 align-top">
                          <div className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 align-top">
                          {user.department || 'Not provided'}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badgeClasses(user.status)}`}>
                            {user.status}
                          </span>
                          {user.role === 'admin' && (
                            <span className="ml-1 inline-flex rounded-full px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm align-top">
                          {user.submissionCount && user.submissionCount > 0 ? (
                            <Link to={`/admin/submissions?staffId=${user.id}`} className="text-blue-600 hover:text-blue-800 font-medium hover:underline">
                              {user.submissionCount}
                            </Link>
                          ) : (
                            <span className="text-gray-500">0</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 align-top">
                          {formatDate(user.lastSubmittedAt || null)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 align-top">
                          <div>{notificationLabel(user)}</div>
                          {user.approvalEmailError && (
                            <div className="mt-1 text-xs text-red-600">{user.approvalEmailError}</div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 align-top">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 align-top">
                          {formatDate(user.lastLoginAt)}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="flex flex-wrap gap-2">
                            {user.id === dashboardUser?.id ? (
                              <span className="text-xs text-gray-400 italic">You</span>
                            ) : null}
                            {user.id !== dashboardUser?.id && user.status !== 'approved' && (
                              <button
                                type="button"
                                disabled={updatingUserId === user.id || deletingUserId === user.id}
                                onClick={() => updateStatus(user.id, 'approved')}
                                className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {updatingUserId === user.id ? 'Saving...' : 'Approve'}
                              </button>
                            )}
                            {user.id !== dashboardUser?.id && user.status === 'approved' && (
                              <button
                                type="button"
                                disabled={updatingUserId === user.id || deletingUserId === user.id}
                                onClick={() => updateStatus(user.id, 'disapproved')}
                                className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                {updatingUserId === user.id ? 'Saving...' : 'Disapprove'}
                              </button>
                            )}
                            {user.id !== dashboardUser?.id && (
                            <button
                              type="button"
                              disabled={updatingUserId === user.id || deletingUserId === user.id || resettingUserId === user.id}
                              onClick={() => resetPassword(user)}
                              className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {resettingUserId === user.id ? 'Resetting...' : 'Reset Password'}
                            </button>
                            )}
                            {user.id !== dashboardUser?.id && user.role !== 'admin' && (
                              <button
                                type="button"
                                disabled={!!promotingUserId || !!demotingUserId}
                                onClick={() => makeAdmin(user)}
                                className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                              >
                                {promotingUserId === user.id ? 'Promoting...' : 'Make Admin'}
                              </button>
                            )}
                            {user.role === 'admin' && user.id !== dashboardUser?.id && (
                              <button
                                type="button"
                                disabled={!!promotingUserId || !!demotingUserId}
                                onClick={() => removeAdmin(user)}
                                className="rounded-lg bg-orange-600 px-3 py-2 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                              >
                                {demotingUserId === user.id ? 'Removing...' : 'Remove Admin'}
                              </button>
                            )}
                            {user.id !== dashboardUser?.id && (
                              <button
                                type="button"
                                disabled={updatingUserId === user.id || deletingUserId === user.id || resettingUserId === user.id}
                                onClick={() => deleteUser(user)}
                                className="rounded-lg bg-slate-700 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                              >
                                {deletingUserId === user.id ? 'Deleting...' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
